import { X } from 'lucide-react'
import { type ReactNode, useEffect, useId } from 'react'
import { createPortal } from 'react-dom'
import { useDragToClose } from '../../lib/use-drag-to-close'
import { useIsMobile } from '../../lib/use-media-query'
import {
  useFocusScrollIntoView,
  useSheetMaxHeight,
  useVisualViewportRect,
} from '../../lib/use-visual-viewport'
import { cn } from '../../lib/utils'

interface DialogProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  className?: string
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
}: DialogProps) {
  const titleId = useId()
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  const isMobile = useIsMobile()
  const drag = useDragToClose(onClose, open && isMobile)
  // Mobile-Sheet: maxHeight am SICHTBAREN Viewport (Tastatur!) statt 95vh.
  // Desktop (sm:) bleibt beim bestehenden Klassen-Verhalten.
  const sheetMaxHeight = useSheetMaxHeight()
  const bodyRef = useFocusScrollIntoView<HTMLDivElement>(open && isMobile)
  // Mobile: Wrapper am sichtbaren Viewport verankern (Tastatur), damit das
  // items-end-Sheet über der Tastatur bleibt statt dahinter.
  const viewport = useVisualViewportRect()

  if (!open) return null
  if (typeof document === 'undefined') return null

  // Portal to body so the dialog escapes any transformed parent (e.g.
  // react-grid-layout items use `transform` which breaks position:fixed).
  // Layout: outer flex centers the panel inside the viewport with a small
  // safety-margin (py-4 px-4). The panel itself is a column with a hard
  // max-height of "viewport minus margin" — header + footer are fixed-size
  // (flex-shrink-0), the body scrolls internally (flex-1 + overflow-y-auto).
  // Without this, tall forms (e.g. Neues Meeting with participant list)
  // overflow above + below the screen and the action buttons become
  // unreachable.
  return createPortal(
    <div
      // phone:*: Handy-Querformat ist ≥640 BREIT, aber per Definition Phone
      // (useIsMobile) — Sheet-Layout muss den JS-Zweigen (drag-to-close,
      // Viewport-Klemme, Safe-Area) folgen, sonst zentriert CSS ein
      // Desktop-Modal mit aktiver Sheet-Mechanik.
      className="app-safe-dialog fixed inset-0 z-50 flex items-end sm:items-center phone:items-end justify-center sm:px-4 sm:py-4 phone:px-0 phone:py-0"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      // Mobil: Wrapper auf den sichtbaren Viewport klemmen (top/height),
      // damit items-end das Sheet über der Tastatur platziert. Desktop
      // (sm:items-center) bleibt volles inset-0.
      style={
        isMobile ? { top: viewport.offsetTop, height: viewport.height, bottom: 'auto' } : undefined
      }
    >
      <button
        type="button"
        className="absolute inset-0 z-0 bg-bg-base/70 backdrop-blur-sm"
        aria-label="Schließen"
        onClick={onClose}
      />
      <div
        className={cn(
          // Phone (hoch + quer, phone:-Overrides): bottom-sheet, voll-Breite,
          // gerundet nur oben. Desktop (sm+): klassisch zentriert mit
          // Außen-Margin.
          // z-10 pinnt das Panel explizit ÜBER den Fullscreen-Backdrop-Button
          // — Hit-Testing darf nie am Backdrop hängenbleiben (D9), auch wenn
          // drag-transform/Animationen eigene Stacking-Contexts erzeugen.
          isMobile && 'app-safe-inline',
          'relative z-10 flex w-full max-h-[95vh] sm:max-h-full flex-col bg-bg-elevated shadow-2xl animate-fade-in',
          'rounded-t-2xl sm:rounded-2xl phone:rounded-b-none',
          'sm:max-w-md phone:max-w-none border-t sm:border phone:border-x-0 phone:border-b-0 border-border-subtle',
          className,
        )}
        // maxHeight nur im Mobile-Zweig überschreiben — inline gewinnt gegen
        // max-h-[95vh]; Desktop behält den unveränderten Klassenpfad.
        style={isMobile ? { ...drag.sheetStyle, maxHeight: sheetMaxHeight } : drag.sheetStyle}
      >
        {/* Drag-Handle nur auf Phones sichtbar (phone:flex statt sm:hidden —
            deckt auch das Querformat ab) — drag-down-to-close-Affordance. */}
        <div
          className="hidden phone:flex justify-center pt-2 pb-1 shrink-0 cursor-grab active:cursor-grabbing"
          {...drag.handlers}
        >
          <div className="w-10 h-1 rounded-full bg-text-tertiary/40" />
        </div>
        <div
          className="flex flex-shrink-0 items-start justify-between gap-4 px-4 sm:px-6 phone:px-4 pt-2 sm:pt-6 phone:pt-2 pb-3"
          {...drag.handlers}
        >
          <div className="min-w-0">
            <h2
              id={titleId}
              className="font-serif text-xl sm:text-2xl phone:text-xl tracking-tight truncate"
            >
              {title}
            </h2>
            {description && <p className="mt-1 text-sm text-text-secondary">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="shrink-0 text-text-tertiary hover:text-text-primary transition-colors p-2.5 -mt-1 -mr-1 rounded-md hover:bg-bg-overlay touch:opacity-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div
          ref={bodyRef}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 phone:px-4 pb-2"
          // Ohne Footer schließt der Scroll-Body das Sheet nach unten ab —
          // dann braucht ER das Home-Indicator-Padding.
          style={
            isMobile && !footer
              ? { paddingBottom: 'calc(0.5rem + var(--app-safe-bottom))' }
              : undefined
          }
        >
          {children}
        </div>
        {footer && (
          <div
            className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border-subtle px-4 sm:px-6 phone:px-4 py-3 sm:py-4 phone:py-3"
            // Safe-Area direkt am Sheet-Footer (statt der fragilen
            // .animate-slide-up-Kopplung); bewusst OHNE display-mode-Query,
            // damit auch der Safari-Browser-Tab mit Bottom-Bar abgedeckt ist.
            style={
              isMobile ? { paddingBottom: 'calc(0.75rem + var(--app-safe-bottom))' } : undefined
            }
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
